// Workbench — the right-hand panel: whatever you open (a page, a PDF, a story, code, a picture, a song, a video),
// beside the conversation. Library files come through /api/workspace/file/:id with the PIN token and are shown
// from memory, so nothing needs a PIN-exempt route.
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BenchItem } from '../../contexts/WorkbenchContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLibraryDelete } from './Places';
import { openLinkFromDesktop } from '../../utils/desktop';
import PadView, { PadData } from './PadView';
import { copyText } from '../MessageBubble';

interface Props {
  item: BenchItem | null; onClose: () => void; onTalkAbout: (item: BenchItem) => void; projects: Array<{ id: string; name: string }>; onFile: (projectId: string, itemId: string) => void;
  /** 📝 The pads, live (a pad on the bench shows the newest of it). */
  pads: PadData[]; emcogName: string; onPadSaved: (pad: PadData) => void; onPadDelete: (pad: PadData) => void;
  /** A pad put on a project (or taken off one: null). */
  onPadFile: (pad: PadData, projectId: string | null) => void;
}

const KIND_LABEL: Record<string, string> = { page: 'PAGE', pdf: 'PDF', text: 'TEXT', code: 'CODE', image: 'ART', audio: 'SONG', video: 'VIDEO', file: 'FILE', pad: 'PAD' };

/** The extension a download takes from what was sent, when the item has no file name of its own. */
const TYPE_EXT: Record<string, string> = {
  'application/pdf': 'pdf', 'text/html': 'html', 'text/plain': 'txt', 'text/markdown': 'md', 'text/csv': 'csv', 'application/json': 'json',
  'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif', 'image/svg+xml': 'svg',
  'audio/mpeg': 'mp3', 'audio/wav': 'wav', 'audio/ogg': 'ogg', 'video/mp4': 'mp4', 'video/webm': 'webm'
};

/**
 * A download is named for its card — "Letter to Richard Dawkins v3 (final, for Ryan's go).pdf", not the stored
 * "letter_to_richard_dawkins_v3_1792….pdf" (Oct 26 2026) — with the file's own extension, and without the characters
 * Windows won't take in a file name.
 */
function savedName(item: BenchItem, contentType: string): string {
  const ext = (/\.([a-z0-9]{1,8})$/i.exec(item.fileName || '')?.[1] || TYPE_EXT[contentType.split(';')[0].trim().toLowerCase()] || '').toLowerCase();
  // eslint-disable-next-line no-control-regex
  const base = item.title.replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').slice(0, 120).trim().replace(/[. ]+$/, '') || 'download';
  return ext && !base.toLowerCase().endsWith(`.${ext}`) ? `${base}.${ext}` : base;
}

/** When a pad last changed, and by whom. */
function padMeta(pad: PadData, emcogName: string): string {
  const mins = Math.round((Date.now() - pad.updatedAt) / 60_000);
  const when = mins < 1 ? 'just now' : mins < 60 ? `${mins} min ago` : mins < 1440 ? `${Math.round(mins / 60)} h ago` : new Date(pad.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Changed ${when} by ${pad.by === 'person' ? 'you' : emcogName}${pad.project ? ` · ${pad.project}` : ''}`;
}

const Workbench: React.FC<Props> = ({ item, onClose, onTalkAbout, projects, onFile, pads, emcogName, onPadSaved, onPadDelete, onPadFile }) => {
  const { token } = useAuth();
  const remove = useLibraryDelete();
  const [src, setSrc] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pad = item?.view === 'pad' ? pads.find(p => p.id === item.padId) || null : null;
  const [editingPad, setEditingPad] = useState(false);
  const [padHistory, setPadHistory] = useState(false);
  useEffect(() => { setEditingPad(false); setPadHistory(false); }, [item?.padId]);
  // The projects a pad can be for: the open ones, and the one it's on even when that one is paused.
  const padProjects = pad?.projectId && !projects.some(p => p.id === pad.projectId) ? [...projects, { id: pad.projectId, name: pad.project || 'Its project' }] : projects;
  const [copiedPad, setCopiedPad] = useState(false);
  const copyPad = async () => {
    if (!pad) return;
    if (await copyText(pad.content)) { setCopiedPad(true); setTimeout(() => setCopiedPad(false), 1600); }
  };

  useEffect(() => {
    setSrc(null); setText(null); setError(null);
    if (!item || item.view === 'pad') return;
    let objectUrl: string | null = null;
    let cancelled = false;
    const load = async () => {
      try {
        const fromLibrary = !item.url && !!item.libraryId;
        const url = item.url || (item.libraryId ? `/api/workspace/file/${encodeURIComponent(item.libraryId)}` : undefined);
        if (!url) return;
        const wantsText = item.view === 'text' || item.view === 'code';
        // A PDF is always shown from memory, as a PDF and nothing else — see body().
        const isPdf = item.view === 'pdf';
        if (!fromLibrary && !wantsText && !isPdf) { setSrc(url); return; }
        const res = await fetch(url, { headers: token && fromLibrary ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new Error(res.status === 404 ? 'That file is no longer where it was.' : `It didn't open (${res.status}).`);
        if (wantsText) { const t = await res.text(); if (!cancelled) setText(t); return; }
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(isPdf ? new Blob([blob], { type: 'application/pdf' }) : blob);
        if (!cancelled) setSrc(objectUrl);
      } catch (e) { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); }
    };
    load();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [item, token]);

  // While a download is fetched the button says so, so a slow one isn't clicked again.
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSaving(false); }, [item]);

  const download = async () => {
    if (!item || saving) return;
    setSaving(true);
    try {
      const fromLibrary = !item.url && !!item.libraryId;
      const url = fromLibrary ? `/api/workspace/file/${encodeURIComponent(item.libraryId!)}?download=1` : item.url!;
      const res = await fetch(url, { headers: token && fromLibrary ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`The download didn't start (${res.status}).`);
      const blob = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = blob; a.download = savedName(item, res.headers.get('Content-Type') || '');
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blob), 4000);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  const body = (): React.ReactNode => {
    if (!item) return null;
    if (error) return <div className="ws-empty">{error}</div>;
    if (item.view === 'pad') return pad ? <PadView pad={pad} emcogName={emcogName} editing={editingPad} onEditing={setEditingPad} onSaved={onPadSaved} history={padHistory} onHistory={setPadHistory} /> : <div className="ws-empty">That pad was taken away.</div>;
    if (item.view === 'text') return text === null ? <div className="ws-empty">Opening…</div> : <div className="ws-doc"><ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown></div>;
    if (item.view === 'code') return text === null ? <div className="ws-empty">Opening…</div> : <pre className="ws-code">{text}</pre>;
    if (item.view === 'file') return <div className="ws-empty">This kind of file can't be shown here. Download it to open it.</div>;
    if (!src) return <div className="ws-empty">Opening…</div>;
    if (item.view === 'image') return <img className="ws-image" src={src} alt={item.title} />;
    if (item.view === 'audio') return <div className="ws-player"><div className="ws-player-art" aria-hidden="true" /><audio controls src={src} /></div>;
    if (item.view === 'video') return <video className="ws-video" controls src={src} />;
    // Not sandboxed: the browser's PDF viewer won't open in a sandboxed frame (it showed only a "blocked" sign). The
    // frame only ever holds the file re-typed as a PDF (load), so nothing in it can run as this page.
    if (item.view === 'pdf') return <iframe className="ws-frame" title={item.title} src={src} />;
    return <iframe className="ws-frame" title={item.title} src={src} sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-modals" />;
  };

  return (
    <aside className={`ws-bench ${item ? 'open' : ''}`} aria-label="Workbench" aria-hidden={!item}>
      {item && (
        <>
          <div className="ws-bench-head">
            <span className={`ws-k ws-k-${item.view}`}>{KIND_LABEL[item.view]}</span>
            <span className="ws-bench-title"><b>{pad ? pad.title : item.title}</b>{pad ? <span>{padMeta(pad, emcogName)}</span> : item.meta && <span>{item.meta}</span>}</span>
            <button className="ws-iconbtn" onClick={onClose} aria-label="Close the workbench" title="Close">✕</button>
          </div>
          <div className={`ws-bench-body ws-view-${item.view}`}>{body()}</div>
          <div className="ws-bench-foot">
            <button className="ws-btn ws-primary" onClick={() => onTalkAbout(item)}>Talk about this</button>
            {pad && !editingPad && !padHistory && <button className="ws-btn" onClick={() => setEditingPad(true)}>Edit</button>}
            {pad && !editingPad && !padHistory && <button className="ws-btn" onClick={copyPad}>{copiedPad ? 'Copied' : 'Copy all'}</button>}
            {pad && !editingPad && (padHistory
              ? <button className="ws-btn" onClick={() => setPadHistory(false)}>Back to the pad</button>
              : (pad.versions || 0) > 0 && <button className="ws-btn" onClick={() => setPadHistory(true)} title="Earlier versions of this pad — any of them can be put back">History</button>)}
            {pad && !editingPad && !padHistory && padProjects.length > 0 && (
              <>
                <label className="ws-sr" htmlFor="ws-pad-project">The project this pad is for</label>
                <select id="ws-pad-project" className="ws-select" value={pad.projectId || ''} onChange={e => onPadFile(pad, e.target.value || null)} title="Picking that project up opens this pad again">
                  <option value="">{pad.projectId ? 'No project' : 'For a project…'}</option>
                  {padProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </>
            )}
            {pad && !editingPad && !padHistory && <button className="ws-btn ws-danger-quiet ws-bench-del" onClick={() => onPadDelete(pad)}>Delete</button>}
            {item.view === 'page' && item.url && <a className="ws-btn" href={item.url} target="_blank" rel="noopener noreferrer" onClick={e => openLinkFromDesktop(e)}>Open in a new tab</a>}
            {(item.url ? item.view !== 'page' : !!item.libraryId) && <button className="ws-btn" onClick={download} disabled={saving}>{saving ? 'Downloading…' : 'Download'}</button>}
            {item.libraryId && projects.length > 0 && (
              <>
                <label className="ws-sr" htmlFor="ws-bench-file">Add to a project</label>
                <select id="ws-bench-file" className="ws-select" value="" onChange={e => { if (e.target.value) onFile(e.target.value, item.libraryId!); }}>
                  <option value="">Add to a project…</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </>
            )}
            {item.libraryId && <button className="ws-btn ws-danger-quiet ws-bench-del" onClick={() => remove({ id: item.libraryId!, title: item.title })}>Delete</button>}
          </div>
        </>
      )}
    </aside>
  );
};

export default Workbench;
